import {createSlice} from '@reduxjs/toolkit';
import {
  FilteredPayloadAction,
  LocationState,
  reducerUtils,
} from '@wallaby-ui/core';

import {DEFAULT_LIST_PAGE_SIZE, LIST_REDUCER_STATE_ID} from './list_constants';
import {
  FilterAndSearchSearchPayload,
  filterAndSearchSlice,
} from 'src/js/containers/filter_and_search';
import {SearchResult} from 'src/js/model/search_result';

export interface GridPagination {
  page: number;
  pages: number;
  pageSize: number;
}

export interface GridParams {
  filters?: any[];
  scroll?: [number, number];
  sort?: any;
  pagination?: GridPagination;
  page?: number;
  size?: number;
  filteredRowsCount?: number;
}

export interface ListResult {
  gridParams: GridParams;
  timestamp?: number;
}

export interface ListState {
  listResult: ListResult;
  locationState: LocationState;
}

export interface ChangeListParamsPayload {
  params: GridParams;
}

const initialState: ListState = {
  listResult: {
    gridParams: {
      filters: [],
      scroll: [0, 0],
      sort: undefined,
      pagination: {
        page: 1,
        pages: 0,
        pageSize: DEFAULT_LIST_PAGE_SIZE,
      },
      page: 1,
      size: DEFAULT_LIST_PAGE_SIZE,
      filteredRowsCount: 0,
    },
    timestamp: undefined,
  },
  locationState: {
    locationParsed: false,
    reload: false,
  },
};

export const listSlice = createSlice({
  name: LIST_REDUCER_STATE_ID,
  initialState,
  reducers: {
    locationParsed: {
      reducer(state, action: FilteredPayloadAction<boolean>) {
        if (state.locationState.locationParsed !== action.payload) {
          state.locationState.locationParsed = action.payload;
        }
      },
      prepare: reducerUtils.prepareWithStateIdFactory<boolean>(),
    },
    resetReload: {
      reducer(state) {
        if (state.locationState.reload !== false) {
          state.locationState.reload = false;
        }
      },
      prepare: reducerUtils.prepareWithStateIdFactory<void>(),
    },
    changeListParams: {
      reducer(state, action: FilteredPayloadAction<ChangeListParamsPayload>) {
        const {params} = action.payload;

        state.listResult.gridParams.pagination = {
          ...state.listResult.gridParams.pagination,
          ...(params.page ? {page: params.page} : {}),
          ...(params.size ? {pageSize: params.size} : {}),
          ...params.pagination,
        };
        state.listResult.gridParams = {
          ...state.listResult.gridParams,
          ...(params.scroll ? {scroll: params.scroll} : {}),
          ...(params.sort ? {sort: [...params.sort]} : {}),
          ...(params.filters ? {filters: [...params.filters]} : {}),
          ...(params.filteredRowsCount
            ? {filteredRowsCount: params.filteredRowsCount}
            : {}),
        };
      },
      prepare:
        reducerUtils.prepareWithStateIdFactory<ChangeListParamsPayload>(),
    },
  },
  extraReducers: (builder) =>
    builder.addCase(
      filterAndSearchSlice.actions.searchSuccess,
      (state, action): ListState => {
        const {response, dynamicResultItemsModels} =
          action.payload as FilterAndSearchSearchPayload;
        // check the timestamp of the result
        if (
          state.listResult &&
          state.listResult.timestamp &&
          response.timestamp
        ) {
          if (state.listResult.timestamp > response.timestamp) {
            return state;
          }
        }
        // paginated result
        if (dynamicResultItemsModels && response.page) {
          const result = new SearchResult(
            response,
            response.name,
            dynamicResultItemsModels
          );
          return {
            ...state,
            listResult: {
              ...state.listResult,
              gridParams: {
                ...state.listResult.gridParams,
                [response.name]: {
                  ...(state.listResult.gridParams
                    ? state.listResult.gridParams[response.name]
                    : {}),
                  pagination: {
                    page: result.getPagination().getNumber() + 1,
                    pages: result.getPagination().getTotalPages(),
                    pageSize: result.getPagination().getSize(),
                  },
                  totalElements: result.getPagination().getTotalElements(),
                },
              },
              timestamp: result.getTimestamp(),
            },
          };
        }
        return {
          ...state,
          listResult: {
            ...state.listResult,
            timestamp: response.timestamp,
          },
        };
      }
    ),
});
